import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  MediaType,
  useCreateMutation,
  useCurrentWorkspace,
  useUpdateMutation,
  useWorkspaceAsset,
  useWorkspaceCategories,
} from '@gripp/shared-logic';
import {
  AddLink,
  Button,
  CategorySelector,
  Colors,
  ControlledTextInput,
  Spacing,
  tokens,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import type { Control } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import * as yup from 'yup';
import { object as yupObject } from 'yup';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { useMediaUpload } from '../../shared/hooks/useMediaUpload';
import { AddAssetMediaType } from './addFilesLinksButton';

const CREATE_MEDIA = `
  mutation createMedia($input: CreateMediaInput!) {
    createMedia(input: $input) {
      id
    }
  }
`;

const CREATE_CATEGORY = `
  mutation createCategory($input: CreateCategoryInput!) {
    createCategory(input: $input) {
      id
      name
    }
  }
`;

const UPDATE_ASSET = `
  mutation UpdateAsset($input: MutateAssetInput!) {
    updateAsset(input: $input) {
      id
    }
  }
`;

/**
 * Delay before the file picker auto-opens. The panel slide-in transition runs
 * ~380ms (300ms animation + 80ms buffer); opening the OS dialog sooner pops it
 * over the still-sliding previous panel instead of the settled form.
 */
const PICKER_AUTO_OPEN_DELAY_MS = 500;

type CategoryItem = { id: string; name: string };

/** Which file's categories the third-column EditCategory panel is editing. */
export type CategoryEditTarget = {
  fileKey: string;
  categories: CategoryItem[];
};

type AssetFileCreateFormProps = {
  type: AddAssetMediaType;
  assetId: string;
  /** Existing media ids on the asset — the new files are appended to these. */
  allFileIds: string[];
  onComplete: () => void;
  /** Open (or close, when already open for it) the category panel for a file. */
  onCategoryEdit?: (target: CategoryEditTarget) => void;
  /** The category panel's current selection, written back into its file. */
  categoryEdit?: CategoryEditTarget | null;
  /** Inline category changes, so an open panel for the same file stays in step. */
  onCategoriesChange?: (target: CategoryEditTarget) => void;
  onCategoryEditClose?: () => void;
};

type PickedFile = { file: File; previewUrl: string };

const LINK_KEY = 'link';

const categoriesSchema = yup
  .array()
  .of(
    yupObject({
      id: yup.string().required(),
      name: yup.string().required(),
    })
  )
  .optional();

const buildSchema = (isLink: boolean) =>
  yupObject({
    linkUrl: isLink ? yup.string().url().required() : yup.string().optional(),
    files: yup
      .array()
      .of(
        yupObject({
          key: yup.string().required(),
          title: yup.string().required(),
          categories: categoriesSchema,
        })
      )
      .required(),
  });

type FormValues = yup.InferType<ReturnType<typeof buildSchema>>;

let pickCounter = 0;
const nextFileKey = () => `file-${++pickCounter}`;

const sameCategoryIds = (a: CategoryItem[], b: CategoryItem[]) =>
  a
    .map((c) => c.id)
    .sort()
    .join(',') ===
  b
    .map((c) => c.id)
    .sort()
    .join(',');

/**
 * Panel form for adding files or a link to an asset. Mirrors
 * `AssetFileEditForm` (the edit-side panel) but creates new media instead of
 * updating it — this is the panelized replacement for the old `CreateFileForm`
 * Modal. Several files can be picked at once; each gets its own numbered name
 * and categories block, and Save adds them all.
 */
export const AssetFileCreateForm = ({
  type,
  assetId,
  allFileIds,
  onComplete,
  onCategoryEdit,
  categoryEdit,
  onCategoriesChange,
  onCategoryEditClose,
}: AssetFileCreateFormProps) => {
  const { t } = useTranslation();
  const isLink = type === 'link';
  const currentWorkspace = useCurrentWorkspace();
  const { categories: availableCategories } = useWorkspaceCategories();
  const { invalidateQuery } = useWorkspaceAsset(assetId);
  const slideLayout = useSlideLayoutSafe();

  const { mutateAsync: createMedia } = useCreateMutation({
    modelName: 'media',
    query: CREATE_MEDIA,
  });
  const { mutateAsync: createCategory } = useCreateMutation({
    modelName: 'category',
    query: CREATE_CATEGORY,
  });
  const { mutateAsync: updateAsset } = useUpdateMutation({
    modelName: 'asset',
    query: UPDATE_ASSET,
  });

  const pickedRef = useRef(new Map<string, PickedFile>());
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const uploadMedia = useMediaUpload({});

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    resolver: yupResolver(buildSchema(isLink)),
    mode: 'onChange',
    defaultValues: {
      linkUrl: '',
      files: isLink ? [{ key: LINK_KEY, title: '', categories: [] }] : [],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'files',
    keyName: 'fieldId',
  });

  // Sync categories chosen in the third-column EditCategory panel back into
  // the file it was opened for.
  useEffect(() => {
    if (!categoryEdit) return;
    const index = getValues('files').findIndex(
      (f) => f.key === categoryEdit.fileKey
    );
    if (index < 0) return;
    const current = getValues(`files.${index}.categories`) ?? [];
    if (!sameCategoryIds(current, categoryEdit.categories)) {
      setValue(`files.${index}.categories`, categoryEdit.categories, {
        shouldDirty: true,
      });
    }
  }, [categoryEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const picked = pickedRef.current;
    return () => {
      picked.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
      picked.clear();
    };
  }, []);

  const handleFileSelected = (file: File) => {
    const key = nextFileKey();
    pickedRef.current.set(key, {
      file,
      previewUrl: URL.createObjectURL(file),
    });
    setErrorMessage(null);
    append({ key, title: file.name, categories: [] }, { shouldFocus: false });
  };

  const removeFiles = (keys: string[]) => {
    const files = getValues('files');
    const indexes = keys
      .map((key) => files.findIndex((f) => f.key === key))
      .filter((index) => index >= 0);
    keys.forEach((key) => {
      const picked = pickedRef.current.get(key);
      if (picked) URL.revokeObjectURL(picked.previewUrl);
      pickedRef.current.delete(key);
    });
    remove(indexes);
    if (categoryEdit && keys.includes(categoryEdit.fileKey)) {
      onCategoryEditClose?.();
    }
  };

  const handleCreateCategory = useCallback(
    async (name: string) => {
      if (!currentWorkspace?.id) {
        throw new Error('Workspace is required to create a category.');
      }
      const result = await createCategory({
        input: { name: name.trim(), workspace: { id: currentWorkspace.id } },
      });
      return { id: result.id, name: result.name };
    },
    [createCategory, currentWorkspace?.id]
  );

  const canSave = isValid && !isSubmitting && fields.length > 0;

  const addToAsset = async (newMediaIds: string[]) => {
    const media = [...allFileIds, ...newMediaIds].map((id) => ({ id }));
    await updateAsset({ input: { id: assetId, media } });
    await invalidateQuery();
  };

  const submitLink = async (data: FormValues) => {
    const [entry] = data.files;
    const result = await createMedia({
      input: {
        name: entry.title,
        type: MediaType.Link,
        extension: 'link',
        status: 'PROCESSED',
        url: data.linkUrl,
        workspaces: [{ id: currentWorkspace?.id }],
        categories: (entry.categories ?? []).map((c) => ({ id: c.id })),
      },
    });
    if (!result?.id) {
      setErrorMessage(t('file.create.linkError'));
      return;
    }
    await addToAsset([result.id]);
    onComplete();
  };

  const submitFiles = async (data: FormValues) => {
    const workspaceId = currentWorkspace?.id;
    const results = await Promise.allSettled(
      data.files.map(async (entry) => {
        const picked = pickedRef.current.get(entry.key);
        if (!picked) throw new Error(`No picked file for ${entry.key}`);
        const mediaId = await uploadMedia(
          picked.file,
          workspaceId,
          entry.title,
          (entry.categories ?? []).map((c) => ({ id: c.id }))
        );
        if (!mediaId) throw new Error(`No media id for ${entry.key}`);
        return { key: entry.key, mediaId };
      })
    );
    const uploaded = results.flatMap((r) =>
      r.status === 'fulfilled' ? [r.value] : []
    );
    results.forEach((r) => {
      if (r.status === 'rejected') {
        console.error('Failed to upload file for asset:', r.reason);
      }
    });

    if (uploaded.length > 0) {
      await addToAsset(uploaded.map((u) => u.mediaId));
    }
    if (uploaded.length === data.files.length) {
      onComplete();
      return;
    }
    // Keep only the files that did not make it, so Save retries just those.
    removeFiles(uploaded.map((u) => u.key));
    setErrorMessage(
      t(
        uploaded.length > 0
          ? 'file.create.partialUploadError'
          : 'file.create.uploadError'
      )
    );
  };

  const onSubmit = async (data: FormValues) => {
    if (!currentWorkspace?.id) return;
    setErrorMessage(null);
    try {
      await (isLink ? submitLink(data) : submitFiles(data));
    } catch (error) {
      console.error('Failed to add file to asset:', error);
      setErrorMessage(
        t(isLink ? 'file.create.linkError' : 'file.create.uploadError')
      );
    }
  };

  return (
    <View style={styles.container}>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        {isLink && (
          <AddLink name="linkUrl" control={control} label="asset.files.link" />
        )}
        {!isLink && fields.length === 0 && (
          <View style={styles.fileField}>
            <MediaPicker
              autoUpload={false}
              autoTrigger
              autoTriggerDelay={PICKER_AUTO_OPEN_DELAY_MS}
              allowsMultipleSelection
              onFileSelected={handleFileSelected}
              setMedia={() => undefined}
              setProgress={() => undefined}
              acceptedMediaTypes={[
                MediaType.Image,
                MediaType.Document,
                MediaType.Video,
              ]}
              workspaceId={currentWorkspace?.id}
              customTrigger={
                <View style={styles.fileUploadArea}>
                  <MaterialCommunityIcons
                    name="plus"
                    size={24}
                    color={Colors.role.textStrong}
                  />
                </View>
              }
            />
          </View>
        )}
        {fields.map((field, index) => (
          <FileEntry
            key={field.fieldId}
            control={control}
            index={index}
            count={fields.length}
            picked={pickedRef.current.get(field.key)}
            availableCategories={availableCategories ?? []}
            onRemove={() => removeFiles([field.key])}
            onCategoriesChange={(categories) => {
              setValue(`files.${index}.categories`, categories, {
                shouldDirty: true,
              });
              onCategoriesChange?.({ fileKey: field.key, categories });
            }}
            onCreateCategory={handleCreateCategory}
            onSelectorEdit={
              onCategoryEdit
                ? () =>
                    onCategoryEdit({
                      fileKey: field.key,
                      categories: getValues(`files.${index}.categories`) ?? [],
                    })
                : slideLayout?.toggleThirdPanel
            }
          />
        ))}
      </ScrollView>

      <View style={styles.footer}>
        {errorMessage && <Text style={styles.errorText}>{errorMessage}</Text>}
        <Button
          testID="asset-file-create-save-button"
          style={styles.saveButton}
          onPress={canSave ? handleSubmit(onSubmit) : undefined}
          state={canSave ? 'active' : 'default'}
          disabled={!canSave}
          size="large"
        >
          {t('file.create.add')}
        </Button>
      </View>
    </View>
  );
};

type FileEntryProps = {
  control: Control<FormValues>;
  index: number;
  count: number;
  picked?: PickedFile;
  availableCategories: CategoryItem[];
  onRemove: () => void;
  onCategoriesChange: (categories: CategoryItem[]) => void;
  onCreateCategory: (name: string) => Promise<CategoryItem>;
  onSelectorEdit?: () => void;
};

/** One picked file's preview, name and categories. */
const FileEntry = ({
  control,
  index,
  count,
  picked,
  availableCategories,
  onRemove,
  onCategoriesChange,
  onCreateCategory,
  onSelectorEdit,
}: FileEntryProps) => {
  const { t } = useTranslation();
  const selectedCategories =
    (useWatch({ control, name: `files.${index}.categories` }) as
      | CategoryItem[]
      | undefined) ?? [];

  return (
    <View
      style={[styles.entry, index > 0 && styles.entryDivider]}
      testID={`asset-file-create-entry-${index}`}
    >
      {count > 1 && (
        <Text style={styles.entryNumber}>
          {t('file.create.fileNumber', { index: index + 1, count })}
        </Text>
      )}
      {picked && (
        <View style={styles.filePreview}>
          <View style={styles.fileImageContainer}>
            {picked.file.type.startsWith('image/') ? (
              <img
                src={picked.previewUrl}
                alt={picked.file.name}
                style={styles.fileImage}
              />
            ) : (
              <MaterialCommunityIcons
                name="file-document"
                size={28}
                color={Colors.role.textStrong}
              />
            )}
          </View>
          <Pressable
            style={styles.closeButton}
            onPress={onRemove}
            testID={
              count > 1
                ? `asset-file-create-remove-file-${index}`
                : 'asset-file-create-remove-file'
            }
            accessibilityRole="button"
            accessibilityLabel={t('file.create.removeFile')}
          >
            <MaterialCommunityIcons
              name="close-circle"
              size={20}
              color={Colors.role.textPrimary}
            />
          </Pressable>
        </View>
      )}

      <View>
        <Text style={styles.label}>{t('asset.files.name')}</Text>
        <ControlledTextInput
          control={control}
          name={`files.${index}.title`}
          placeholder={t('asset.files.name')}
          hideBorder
        />
      </View>

      <CategorySelector
        selectedCategories={selectedCategories}
        availableCategories={availableCategories}
        onCategoriesChange={onCategoriesChange}
        onCreateCategory={onCreateCategory}
        onSelectorEdit={onSelectorEdit}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  scrollContent: {
    padding: Spacing.basePadding.base,
    gap: 14,
  },
  entry: {
    gap: 14,
  },
  entryDivider: {
    borderTopWidth: 1,
    borderTopColor: Colors.role.borderSubtle,
    paddingTop: 14,
  },
  entryNumber: {
    ...tokens.typography.label.mediumEmphasised,
    color: Colors.role.textStrong,
  },
  fileField: {
    alignItems: 'flex-start',
  },
  label: {
    ...tokens.typography.body.small,
    fontWeight: '600',
    marginBottom: 8,
    color: Colors.role.textStrong,
  },
  fileUploadArea: {
    width: 80,
    height: 80,
    borderRadius: 8,
    backgroundColor: Colors.role.surfaceRaised,
    alignItems: 'center',
    justifyContent: 'center',
  },
  filePreview: {
    position: 'relative',
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: Colors.role.surfaceRaised,
  },
  fileImageContainer: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  closeButton: {
    position: 'absolute',
    top: 4,
    right: 4,
    zIndex: 1,
  },
  errorText: {
    ...tokens.typography.body.small,
    color: Colors.role.textDestructive,
  },
  footer: {
    padding: Spacing.basePadding.base,
    gap: 8,
  },
  saveButton: {
    borderRadius: 2,
    width: '100%',
    height: 48,
  },
});
